import React, { useState, useMemo } from 'react';
import { AppUser, UserRole, UserStatus, UserCsvRowProcessResult } from '../../../types';
import { SpinnerIcon, UsersIconSolid, SearchIcon } from '../../../components/Icons';
import { useConnection } from '../../../context/ConnectionContext';
import { ChangeUserPasswordModal } from '../../../components/admin/ChangeUserPasswordModal';
import { AdminUserCsvUploadSection } from './AdminUserCsvUploadSection';

interface AdminUserManagementProps {
  users: AppUser[];
  isLoadingUsers: boolean;
  message: string | null;
  updateOpStatus: { [userId: string]: { loading: boolean; error: string | null; success: boolean } };
  onOpenUserModal: (user?: AppUser) => void;
  onChangeUserStatus: (userId: string, currentStatus: UserStatus, username: string) => Promise<void>;
  onRefreshUsers: () => void;
  userCsvFile: File | null;
  onUserCsvFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onProcessUserCsv: () => void;
  onDownloadUserTemplate: () => void;
  isProcessingUserCsv: boolean;
  userCsvProcessingMessage: string | null;
  userCsvResults: UserCsvRowProcessResult[];
  isProcessingComplete: boolean;
}

export const AdminUserManagement: React.FC<AdminUserManagementProps> = ({
  users,
  isLoadingUsers,
  message,
  updateOpStatus,
  onOpenUserModal,
  onChangeUserStatus,
  userCsvFile,
  onUserCsvFileChange,
  onProcessUserCsv,
  onDownloadUserTemplate,
  isProcessingUserCsv,
}) => {
  const { isOffline } = useConnection();
  const [searchTerm, setSearchTerm] = useState('');
  const [passwordModalUser, setPasswordModalUser] = useState<AppUser | null>(null);

  const filteredUsers = useMemo(() => {
    if (!users || !Array.isArray(users)) return [];
    if (!searchTerm.trim()) return users;
    const lowercasedFilter = searchTerm.toLowerCase();
    return users.filter(user =>
      Boolean(user) && (
        (user.displayName || '').toLowerCase().includes(lowercasedFilter) ||
        (user.username || '').toLowerCase().includes(lowercasedFilter) ||
        (user.email || '').toLowerCase().includes(lowercasedFilter) ||
        (user.role || '').toLowerCase().includes(lowercasedFilter)
      )
    );
  }, [users, searchTerm]);

  const getRoleBadgeStyle = (role?: UserRole | string | null) => {
    switch (role) {
      case UserRole.SUPERADMIN: return 'bg-red-100 text-red-800 border-red-200';
      case UserRole.ADMIN: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case UserRole.OPERACION: return 'bg-orange-100 text-orange-800 border-orange-200';
      case UserRole.BROKER: return 'bg-sky-100 text-sky-800 border-sky-200';
      case UserRole.BROKER_PREMIUM: return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case UserRole.BROKER_INTERNO: return 'bg-blue-100 text-blue-800 border-blue-200';
      case UserRole.BROKER_EXTERIOR: return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case UserRole.BROKERS_LEADS: return 'bg-violet-100 text-violet-800 border-violet-200';
      case UserRole.CLIENTE: return 'bg-purple-100 text-purple-800 border-purple-200';
      case UserRole.COMERCIAL_INTERNO: return 'bg-teal-100 text-teal-800 border-teal-200';
      case UserRole.SIN_ROL: return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusBadgeStyle = (status?: UserStatus | string | null) => {
    switch (status) {
      case UserStatus.ACTIVE: return 'bg-green-100 text-green-800 border-green-200';
      case UserStatus.INACTIVE: return 'bg-red-100 text-red-800 border-red-200';
      case UserStatus.PENDING: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoadingUsers && (!users || users.length === 0)) {
    return (
      <div className="flex items-center justify-center py-20">
        <SpinnerIcon className="w-10 h-10 text-sky-600 mr-3" />
        <span className="font-bold text-gray-500 uppercase tracking-widest text-xs">Cargando usuarios...</span>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h3 className="text-xl font-black text-gray-800 uppercase tracking-tight">Gestión de Usuarios y Roles</h3>
        <p className="text-sm text-gray-500 mt-1">Administra los accesos, contraseñas, roles y estados de los usuarios.</p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-xl text-sm border ${message.toLowerCase().includes('error') ? 'bg-red-50 text-red-700 border-red-100' : 'bg-sky-50 text-sky-700 border-sky-100'}`}>
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 space-y-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
              <SearchIcon className="h-5 w-5" />
            </div>
            <input
              type="text"
              className="block w-full pl-11 pr-4 py-3 border-2 border-gray-100 rounded-2xl focus:ring-2 focus:ring-sky-200 focus:border-sky-500 outline-none transition-all text-sm shadow-sm"
              placeholder="Buscar por nombre, username o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => onOpenUserModal()}
            disabled={isOffline}
            className="px-6 py-3 bg-[#0073b7] hover:bg-sky-700 text-white rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 disabled:opacity-50 flex items-center gap-2"
          >
            <UsersIconSolid className="w-4 h-4" />
            Nuevo Usuario
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Usuario</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Rol</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Estado</th>
                <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.map((user) => {
                if (!user) return null;
                const userRole = user.role ? String(user.role).replace(/_/g, ' ') : 'Sin Rol';
                const userStatus = user.status || UserStatus.PENDING;
                const isUserLoading = Boolean(user.id && updateOpStatus?.[user.id]?.loading);
                return (
                  <tr key={user.id || user.email || Math.random().toString()} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-800">{user.displayName || user.username || 'Sin Nombre'}</span>
                        <span className="text-xs text-gray-400 font-mono">{user.email || 'Sin correo'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-[10px] font-black uppercase rounded-lg border ${getRoleBadgeStyle(user.role)}`}>
                        {userRole}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-[10px] font-black uppercase rounded-lg border ${getStatusBadgeStyle(user.status)}`}>
                        {userStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => onOpenUserModal(user)}
                        className="text-sky-600 hover:text-sky-800 text-xs font-bold uppercase tracking-widest"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => setPasswordModalUser(user)}
                        className="text-amber-600 hover:text-amber-800 text-xs font-bold uppercase tracking-widest"
                        title="Cambiar contraseña de este usuario"
                      >
                        Clave
                      </button>
                      <button
                        onClick={() => onChangeUserStatus(user.id, user.status || UserStatus.INACTIVE, user.username || user.email || '')}
                        disabled={isUserLoading}
                        className={`text-xs font-bold uppercase tracking-widest ${user.status === UserStatus.ACTIVE ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-800'}`}
                      >
                        {isUserLoading ? <SpinnerIcon className="w-3 h-3 inline"/> : (user.status === UserStatus.ACTIVE ? 'Bajar' : 'Activar')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredUsers.length === 0 && (
        <div className="py-20 text-center">
          <SearchIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">No se encontraron usuarios</p>
        </div>
      )}

      <AdminUserCsvUploadSection
        userCsvFile={userCsvFile}
        onUserCsvFileChange={onUserCsvFileChange}
        onProcessUserCsv={onProcessUserCsv}
        onDownloadUserTemplate={onDownloadUserTemplate}
        isProcessingUserCsv={isProcessingUserCsv}
      />

      <ChangeUserPasswordModal
        user={passwordModalUser}
        isOpen={Boolean(passwordModalUser)}
        onClose={() => setPasswordModalUser(null)}
      />
    </div>
  );
};
